import React, { useEffect, useState } from 'react';
import { Modal, FormLayout, TextField, InlineStack, Badge, Text, BlockStack } from '@shopify/polaris';
import { configApi } from '../../../services/api/configApi';

export function BearerTokenCredentialsModal({
  open,
  onClose,
  onSave,
  configKey,
  title = 'Bearer Token Connection',
  defaultConnectionName = '',
  defaultBaseUrl = '',
  defaultTestPath = '/v1/health',
  config = {}
}) {
  const [formData, setFormData] = useState({
    connectionName: defaultConnectionName,
    baseUrl: defaultBaseUrl,
    token: '',
    testPath: defaultTestPath
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState({ ok: null, message: '' });

  useEffect(() => {
    if (!open) return;
    setFormData({
      connectionName: config.connectionName || defaultConnectionName,
      baseUrl: config.baseUrl || defaultBaseUrl,
      token: config.token || '',
      testPath: config.testPath || defaultTestPath
    });
    setStatus({ ok: null, message: '' });
  }, [open, config, defaultConnectionName, defaultBaseUrl, defaultTestPath]);

  const canSave = !!formData.connectionName.trim() && !!formData.baseUrl.trim() && !!formData.token.trim();

  const payload = () => ({
    connectionName: formData.connectionName.trim(),
    baseUrl: formData.baseUrl.trim().replace(/\/$/, ''),
    token: formData.token === '****' ? undefined : formData.token,
    testPath: formData.testPath.trim() || defaultTestPath
  });

  const handleSave = async () => {
    setSaving(true);
    setStatus({ ok: null, message: '' });
    try {
      await configApi.saveBearerCredentials(configKey, payload());
      setStatus({ ok: true, message: 'Connection saved' });
      if (onSave) await onSave();
      onClose();
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Save failed' });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setStatus({ ok: null, message: 'Testing connection...' });
    try {
      const result = await configApi.validateCredentials(configKey, {
        authMethod: 'bearer',
        ...payload()
      });
      if (result.success && result.valid) {
        setStatus({ ok: true, message: result.message || 'Connection successful' });
      } else {
        setStatus({ ok: false, message: result.error || 'Connection failed' });
      }
    } catch (error) {
      setStatus({ ok: false, message: error.message || 'Connection failed' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      primaryAction={{
        content: 'Save',
        onAction: handleSave,
        disabled: !canSave,
        loading: saving
      }}
      secondaryActions={[
        { content: 'Test connection', onAction: handleTest, disabled: !canSave || testing, loading: testing },
        { content: 'Cancel', onAction: onClose }
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <FormLayout>
            <TextField
              label="Connection name"
              value={formData.connectionName}
              onChange={(value) => setFormData(prev => ({ ...prev, connectionName: value }))}
              autoComplete="off"
            />
            <TextField
              label="URL"
              value={formData.baseUrl}
              onChange={(value) => setFormData(prev => ({ ...prev, baseUrl: value }))}
              placeholder="http://localhost:3100"
              autoComplete="off"
            />
            <TextField
              label="Bearer token"
              value={formData.token}
              onChange={(value) => setFormData(prev => ({ ...prev, token: value }))}
              type="password"
              autoComplete="off"
            />
            <TextField
              label="Test path"
              value={formData.testPath}
              onChange={(value) => setFormData(prev => ({ ...prev, testPath: value }))}
              placeholder="/v1/health"
              autoComplete="off"
            />
          </FormLayout>

          {status.message && (
            <InlineStack gap="200">
              <Badge tone={status.ok ? 'success' : status.ok === false ? 'critical' : 'info'}>
                {status.ok ? 'Connected' : status.ok === false ? 'Failed' : 'Testing'}
              </Badge>
              <Text as="span">{status.message}</Text>
            </InlineStack>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
